<script setup lang="ts">
import { VariantProps, cva } from "class-variance-authority"
import {
  SwitchRoot,
  type SwitchRootEmits,
  type SwitchRootProps,
  SwitchThumb,
  useForwardPropsEmits,
} from "reka-ui"

export interface UiSwitchProps extends SwitchRootProps {
  class?: ClassValue
  classes?: { root?: ClassValue; thumb?: ClassValue }
  size?: VariantProps<typeof rootVariant>["size"]
}

const props = defineProps<UiSwitchProps>()
const emits = defineEmits<SwitchRootEmits>()

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)

const rootVariant = cva("", {
  variants: {
    size: {
      small: "h-4 w-7",
      medium: "h-6 w-11",
      large: "h-8 w-15",
    },
  },
  defaultVariants: {
    size: "medium",
  },
})

const thumbVariant = cva("", {
  variants: {
    size: {
      small: "h-3 w-3 data-[state=checked]:translate-x-3 rtl:data-[state=checked]:-translate-x-3",
      medium: "h-5 w-5 data-[state=checked]:translate-x-5 rtl:data-[state=checked]:-translate-x-5",
      large: "h-7 w-7 data-[state=checked]:translate-x-7 rtl:data-[state=checked]:-translate-x-7",
    },
  },
  defaultVariants: {
    size: "medium",
  },
})
</script>

<template>
  <SwitchRoot
    v-bind="forwarded"
    :class="
      cn(
        'focus-visible:ring-ring focus-visible:ring-offset-background data-[state=checked]:bg-theme data-[state=unchecked]:bg-input peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        rootVariant({ size }),
        props.classes?.root,
        props.class
      )
    "
  >
    <slot />

    <SwitchThumb
      :class="
        cn(
          'bg-background pointer-events-none block rounded-full shadow-lg ring-0 transition-transform',
          thumbVariant({ size }),
          props.classes?.thumb
        )
      "
    >
      <slot name="thumb" />
    </SwitchThumb>
  </SwitchRoot>
</template>
